/**
 * Azure AD (Entra ID) Configuration
 *
 * Using your existing Aviator Energy Azure AD tenant.
 * Investors can be added as Guest users in Azure AD.
 *
 * App Registration: Aviator Investor Portal
 * Sign-in Audience: AzureADandPersonalMicrosoftAccount
 */

// ============================================
// AZURE AD CONFIGURATION (CONFIGURED)
// ============================================
const TENANT_ID = '60895329-770e-4a73-ad30-08f842948516';  // Aviator Energy tenant
const CLIENT_ID = 'a0256ce7-1b5c-4995-91e2-fe54ec6570b1';  // Investor Portal app
// ============================================

/**
 * MSAL Configuration for Azure AD
 */
export const msalConfig = {
    auth: {
        clientId: CLIENT_ID,
        authority: `https://login.microsoftonline.com/${TENANT_ID}`,
        redirectUri: window.location.origin,
        postLogoutRedirectUri: window.location.origin,
    },
    cache: {
        cacheLocation: 'sessionStorage',
        storeAuthStateInCookie: false,
    },
};

/**
 * Scopes for login
 */
export const loginRequest = {
    scopes: ['openid', 'profile', 'email'],
};

function idTokenExpiring(token) {
    try {
        const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        return !payload.exp || (payload.exp * 1000) < Date.now() + 2 * 60 * 1000;
    } catch {
        return true;
    }
}

/**
 * The signed-in user's Entra ID token for API calls that check it (currently
 * /api/decks - see api/shared/require-investor.js). Returns the raw JWT, or null
 * if there is no signed-in account. Send it as the X-Aviator-ID-Token header:
 * Static Web Apps overwrites Authorization before the request reaches the API.
 *
 * Ported from the Daily Dashboard. MSAL v3 gotcha: acquireTokenSilent caches off
 * the ACCESS token's lifetime (60-90 min) but the ID token only lives 60 min, so
 * it can hand back an already-expired ID token that the API rejects. Check the
 * ID token's own exp and force a refresh when it is stale. If silent refresh
 * fails outright the session is gone, so send the user back through sign-in -
 * once, guarded by a sessionStorage flag so a failing login can't redirect-loop.
 */
export async function getApiToken(instance) {
    const account = instance.getActiveAccount() || instance.getAllAccounts()[0];
    if (!account) return null;
    try {
        let result = await instance.acquireTokenSilent({ ...loginRequest, account });
        if (!result.idToken || idTokenExpiring(result.idToken)) {
            result = await instance.acquireTokenSilent({ ...loginRequest, account, forceRefresh: true });
        }
        sessionStorage.removeItem('ip_auth_redirected');
        return result.idToken || null;
    } catch {
        if (!sessionStorage.getItem('ip_auth_redirected')) {
            sessionStorage.setItem('ip_auth_redirected', '1');
            try { await instance.acquireTokenRedirect({ ...loginRequest, account }); } catch {}
        }
        return null;
    }
}

/**
 * API configuration
 */
export const apiConfig = {
    // Azure Functions API URL
    baseUrl: import.meta.env.VITE_API_URL || '/api',
};
